import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPostBySlug } from '../lib/blogStorage';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);

  useEffect(() => {
    let active = true;

    getPostBySlug(slug)
      .then((data) => {
        if (!active) return;
        if (!data) {
          navigate('/blog', { replace: true });
          return;
        }
        setPost(data);
      })
      .catch((err) => {
        console.error('Failed to load post', err);
        navigate('/blog', { replace: true });
      });

    return () => {
      active = false;
    };
  }, [slug, navigate]);

  if (!post) return null;

  return (
    <div className="relative min-h-screen bg-zinc-50 pb-32">
      <Helmet>
        <title>{post.title} – Synex Agency</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content="creative agency, web design, AI automation, digital marketing, branding" />
        <meta property="og:title" content={`${post.title} – Synex Agency`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://synex.agency/blog/${post.slug}`} />
        <meta property="og:image" content="https://synex.agency/assets/synex.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} – Synex Agency`} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content="https://synex.agency/assets/synex.png" />
        <link rel="canonical" href={`https://synex.agency/blog/${post.slug}`} />
      </Helmet>

      {/* Styled Hero Background */}
      <div className="absolute inset-x-0 top-0 h-[60vh] bg-brand rounded-b-[4rem] sm:rounded-b-[8rem] lg:rounded-b-[12rem] shadow-xl shadow-brand/20 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-brand via-brand/80 to-brand/60 rounded-b-[4rem] sm:rounded-b-[8rem] lg:rounded-b-[12rem] opacity-95"></div>
      </div>

      <div className="relative z-10 pt-32 max-w-5xl mx-auto px-4 sm:px-6">
        <div className="mb-10 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-2.5 text-sm font-medium text-white shadow-sm backdrop-blur-md transition-all hover:bg-white hover:text-brand"
          >
            ← Back to blog
          </Link>
        </div>

        {/* Floating Card Container */}
        <div className="overflow-hidden rounded-[3rem] sm:rounded-[4rem] border border-zinc-200/50 bg-white p-2 sm:p-4 shadow-2xl shadow-black/10 transition-transform duration-500 hover:-translate-y-1">
          <div className="rounded-[2.5rem] sm:rounded-[3.5rem] bg-zinc-50/50 px-6 py-12 sm:px-12 sm:py-20 lg:px-20 lg:py-24 border border-zinc-100">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-900 leading-[1.15]">
              {post.title}
            </h1>

            <p className="mt-8 text-xl text-zinc-500 leading-relaxed max-w-3xl border-l-4 border-brand pl-6 font-medium">
              {post.excerpt}
            </p>

            <div className="mt-16 border-t border-zinc-200/80 pt-16">
              <article className="prose prose-lg prose-zinc max-w-none hover:prose-a:text-brand">
                {post.content.split('\n').map((line, index) => (
                  <p key={index} className="text-zinc-600 leading-loose mb-6">
                    {line.trim()}
                  </p>
                ))}
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
